import { Progress } from "@shared/ui";
import { Flex } from "antd";
import { getScoreColor } from "../lib/aiFitCheckUtils";
import styles from "./styles.module.scss";

export function BulletList({ items }: { items?: string[] }) {
  if (!items?.length) {
    return <p className={styles.emptyText}>No items</p>;
  }
  return (
    <ul className={styles.bulletList}>
      {items.map((item, i) => (
        <li key={`${i}-${item.slice(0, 24)}`} className={styles.bulletItem}>
          <span className={styles.bulletDot}>•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function ScoreRow({
  label,
  score,
  reason,
}: {
  label: string;
  score: number;
  reason: string;
}) {
  const clamped = Math.min(100, Math.max(0, score ?? 0));
  return (
    <div className={styles.scoreCard}>
      <Flex align="center" justify="space-between" gap={12}>
        <span className={styles.scoreLabel}>{label}</span>
        <span
          className={styles.scoreValue}
          style={{ color: getScoreColor(clamped) }}
        >
          {clamped}
        </span>
      </Flex>
      <Progress
        percent={clamped}
        showInfo={false}
        size="small"
        strokeColor={getScoreColor(clamped)}
      />
      {reason ? <p className={styles.scoreReason}>{reason}</p> : null}
    </div>
  );
}

type BadgeVariant = "positive" | "warning" | "fit" | "not-fit";

export function BadgeList({
  items,
  variant,
}: {
  items?: string[];
  variant: BadgeVariant;
}) {
  if (!items?.length) return <p className={styles.emptyText}>—</p>;
  return (
    <Flex wrap="wrap" gap={8}>
      {items.map((item, i) => (
        <span
          key={`${i}-${item.slice(0, 20)}`}
          className={`${styles.badge} ${styles[`badge_${variant}`]}`}
        >
          {item}
        </span>
      ))}
    </Flex>
  );
}

export function DetailRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className={styles.detailRow}>
      <dt className={styles.detailLabel}>{label}</dt>
      <dd className={styles.detailValue}>{value || "—"}</dd>
    </div>
  );
}

export function AudienceCard({
  label,
  match,
}: {
  label: string;
  match?: {
    score: number;
    campaign_need: string;
    creator_data: string;
    reason: string;
  };
}) {
  if (!match) return null;
  return (
    <div className={styles.audienceCard}>
      <Flex align="center" justify="space-between">
        <span className={styles.audienceLabel}>{label}</span>
        <span
          className={styles.scoreValue}
          style={{ color: getScoreColor(match.score) }}
        >
          {match.score}
        </span>
      </Flex>
      <dl className={styles.audienceGrid}>
        <div>
          <dt className={styles.audienceMetaLabel}>Campaign need</dt>
          <dd className={styles.audienceMetaValue}>{match.campaign_need}</dd>
        </div>
        <div>
          <dt className={styles.audienceMetaLabel}>Creator data</dt>
          <dd className={styles.audienceMetaValue}>{match.creator_data}</dd>
        </div>
      </dl>
      {match.reason ? (
        <p className={styles.audienceReason}>{match.reason}</p>
      ) : null}
    </div>
  );
}

export function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricValue}>{value}</div>
      <div className={styles.metricLabel}>{label}</div>
    </div>
  );
}
